import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialogAtom } from '../../atoms/ConfirmDialog/ConfirmDialogAtom';

import { Sphere } from 'types/global';
import {
  createRelationship,
  deleteRelationship,
  type DeleteRelationshipParams
} from '@center-platform/spheres/sphere';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';

import './PeriodDisplayMolecule.scss';

interface PeriodDisplayProps {
  sphereId: string;
  editable?: boolean;
  collapsible?: boolean;
  onPeriodsChange?: () => void;
  periodRelationships?: PeriodRelationship[];
  relationshipsLoading?: boolean;
  relationshipsError?: string | null;
  onShowPeriodSelector?: () => void;
}

interface PeriodRelationship {
  id: string;
  relationshipType: string;
  sourceId: string;
  targetId: string;
  active: boolean;
  createdAt: string;
  createdBy: string;
  deactivatedAt?: string;
  deactivatedBy?: string;
  metadata?: Record<string, any>;
  targetSphere?: Sphere;
}

const PeriodDisplayMolecule: React.FC<PeriodDisplayProps> = ({
  sphereId,
  editable = false,
  collapsible = true,
  onPeriodsChange,
  periodRelationships = [],
  relationshipsLoading = false,
  relationshipsError = null,
  onShowPeriodSelector
}) => {
  const apiClients = useApiClientsOrNull();
  const toastRef = useRef<Toast>(null);

  if (!apiClients) {
    return null;
  }

  const { graphClient } = apiClients;

  // State - no longer managing relationships internally
  const [selectedPeriods, setSelectedPeriods] = useState<Sphere[]>([]);
  const [collapsed, setCollapsed] = useState(false);

  // Process passed period relationships to extract target spheres
  useEffect(() => {
    console.log('🎯 PeriodDisplay - Processing passed relationships:', periodRelationships);

    // Extract the target spheres for display
    const periods = periodRelationships.map(rel => rel.targetSphere).filter(Boolean) as Sphere[];
    setSelectedPeriods(periods);

    console.log('🎯 PeriodDisplay - Extracted periods for display:', periods);
  }, [periodRelationships]);

  // Handle period selection from modal
  const handlePeriodSelection = useCallback(async (periods: Sphere[]) => {
    console.log('🎯 PeriodDisplay - handlePeriodSelection:', periods);

    try {
      // Find periods to add (not currently selected)
      const currentPeriodIds = selectedPeriods.map(p => p.id);
      const newPeriods = periods.filter(p => !currentPeriodIds.includes(p.id));

      // Find periods to remove (currently selected but not in new selection)
      const newPeriodIds = periods.map(p => p.id);
      const periodsToRemove = periodRelationships.filter(
        rel => !newPeriodIds.includes(rel.targetId)
      );

      // Create relationships for new periods
      for (const period of newPeriods) {
        console.log('➕ Creating relationship for period:', period.properties?.name);
        const createResult = await createRelationship(graphClient, {
          fromSphereId: sphereId,
          toSphereId: period.id,
          relationshipType: 'applies_to_period',
        });

        if (!createResult.success) {
          throw new Error(createResult.error || 'Failed to create period relationship');
        }
      }

      // Remove relationships for deselected periods
      for (const rel of periodsToRemove) {
        console.log('➖ Removing relationship for period:', rel.targetSphere?.properties?.name);
        const deleteParams: DeleteRelationshipParams = {
          relationshipId: rel.id,
        };

        const deleteResult = await deleteRelationship(graphClient, deleteParams);
        if (!deleteResult.success) {
          throw new Error(deleteResult.error || 'Failed to remove period relationship');
        }
      }

      // Show success message
      const addedCount = newPeriods.length;
      const removedCount = periodsToRemove.length;
      let message = 'Period assignments updated successfully';
      if (addedCount > 0 && removedCount > 0) {
        message = `Added ${addedCount} and removed ${removedCount} period assignments`;
      } else if (addedCount > 0) {
        message = `Added ${addedCount} period assignment${addedCount > 1 ? 's' : ''}`;
      } else if (removedCount > 0) {
        message = `Removed ${removedCount} period assignment${removedCount > 1 ? 's' : ''}`;
      }

      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: message,
        life: 3000,
      });

      // Notify parent to refresh relationships
      onPeriodsChange?.();

    } catch (error: any) {
      console.error('Failed to update period relationships:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to update period assignments',
        life: 5000,
      });
    }
  }, [sphereId, selectedPeriods, periodRelationships, graphClient, onPeriodsChange]);

  // Remove individual period with confirmation
  const handleRemovePeriod = useCallback((period: Sphere) => {
    console.log('🗑️ PeriodDisplay - Remove period:', period.properties?.name);
    
    confirmDialogAtom({
      message: `Are you sure you want to remove the period assignment for "${period.properties?.name || 'Unknown Period'}"?`,
      header: 'Confirm Remove Period',
      icon: 'pi pi-exclamation-triangle',
      variant: 'danger',
      acceptLabel: 'Remove',
      rejectLabel: 'Cancel',
      accept: async () => {
        console.log('🗑️ PeriodDisplay - User confirmed period removal');
        
        try {
          // Find the relationship for this period
          const relationship = periodRelationships.find(rel => rel.targetId === period.id);
          if (!relationship) {
            throw new Error('Relationship not found for this period');
          }

          const deleteParams: DeleteRelationshipParams = {
            relationshipId: relationship.id,
          };
          
          const result = await deleteRelationship(graphClient, deleteParams);
          if (!result.success) {
            throw new Error(result.error || 'Failed to remove period assignment');
          }

          toastRef.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: `Removed period assignment: ${period.properties?.name}`,
            life: 3000,
          });

          // Notify parent to refresh relationships
          onPeriodsChange?.();

        } catch (error: any) {
          console.error('Failed to remove period:', error);
          toastRef.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to remove period assignment',
            life: 5000,
          });
        }
      },
      reject: () => {
        console.log('🗑️ PeriodDisplay - Period removal cancelled by user');
      }
    });
  }, [periodRelationships, graphClient, onPeriodsChange]);

  // Get period tag severity based on timeframe type
  const getPeriodTagSeverity = (period: Sphere) => {
    const timeframeType = (period.properties as any)?.timeframeTypeKey?.replace('center_calendar_', '');
    switch (timeframeType) {
      case 'year': return 'success';
      case 'quarter': return 'info'; 
      case 'month': return 'warning';
      default: return 'secondary';
    }
  };

  // Render period chips
  const renderPeriodChips = () => {
    if (relationshipsLoading) {
      return (
        <div className="flex flex-wrap gap-2">
          <Skeleton key="period-skeleton-1" width="120px" height="28px" borderRadius="14px" />
          <Skeleton key="period-skeleton-2" width="120px" height="28px" borderRadius="14px" />
          <Skeleton key="period-skeleton-3" width="120px" height="28px" borderRadius="14px" />
        </div>
      );
    }

    if (selectedPeriods.length === 0) {
      return (
        <div className="period-empty-state text-color-secondary">
          {editable ? 
            'Select periods to define when this sphere applies' : 
            'No periods assigned'
          }
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-2">
        {selectedPeriods.map((period) => (
          <Tag
            key={period.id}
            value={(period.properties as any)?.name || 'Unknown Period'}
            severity={getPeriodTagSeverity(period)}
            className="period-chip"
            icon={editable ? 'pi pi-times' : undefined}
            onClick={editable ? () => handleRemovePeriod(period) : undefined}
            style={{ 
              cursor: editable ? 'pointer' : 'default',
              paddingRight: editable ? '2rem' : '0.75rem'
            }}
          />
        ))}
      </div>
    );
  };

  // Header content
  const headerContent = (
    <div className="flex justify-content-between align-items-center">
      <h3 className="m-0 text-lg font-semibold">
        Applied Periods ({selectedPeriods.length})
      </h3>
      <div className="flex gap-2 align-items-center">
        {editable && (
          <Button
            type="button"
            label="Select Periods"
            icon="pi pi-calendar"
            className="p-button-outlined p-button-sm"
            onClick={() => onShowPeriodSelector?.()}
          />
        )}
        {collapsible && (
          <Button
            icon={collapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'}
            className="p-button-text p-button-sm"
            onClick={() => setCollapsed(!collapsed)}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="period-display-molecule">
      <Toast ref={toastRef} />

      <div className="p-card p-3">
        {headerContent}
        
        {!collapsed && (
          <div className="mt-3">
            {relationshipsError ? (
              <div className="text-color-danger">
                <i className="pi pi-exclamation-triangle mr-2" />
                {relationshipsError}
              </div>
            ) : (
              renderPeriodChips()
            )}
          </div>
        )}
      </div>

      {/* Period Selector now handled by parent via onShowPeriodSelector */}
    </div>
  );
};

export default PeriodDisplayMolecule;